// src/services/authApi.ts - Enhanced with billing and registration codes
import api from './api';
import { User, RegisterData } from '../contexts/AuthContext';
import { CompanyType } from '../types/companies';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  user: User;
}

export interface RegisterResponse {
  success: boolean;
  message: string;
  requiresApproval: boolean;
}

// Enhanced Company interface with billing fields
export interface Company {
  companyId: number;
  companyType: CompanyType;
  companyName: string;
  contactEmail: string;
  contactPhone: string;
  address: string;
  city: string;
  postCode: string;
  isActive: boolean;
  createdDate: string;
  userCount: number;
  activeUserCount: number;
  deviceCount: number;
  
  // NEW: Billing Information
  dateOfSale?: string;
  monthlyRental?: number;
  otherCharges?: number;
  devicesLicensed?: number;
  registrationCode?: string;
  registrationCodeUsed?: boolean;
  registrationCodeUsedDate?: string;
}

// NEW: Billing and Registration Code DTOs
export interface CreateCustomerCompanyRequest {
  companyName: string;
  contactEmail: string;
  contactPhone?: string;
  address?: string;
  city?: string;
  postCode?: string;
  dateOfSale: string;
  monthlyRental: number;
  otherCharges?: number;
  devicesLicensed: number;
}

export interface UpdateCompanyRequest {
  companyName: string;
  contactEmail: string;
  contactPhone?: string;
  address?: string;
  city?: string;
  postCode?: string;
  dateOfSale?: string;
  monthlyRental?: number;
  otherCharges?: number;
  devicesLicensed?: number;
}

export interface CreateCompanyResponse {
  companyId: number;
  companyName: string;
  registrationCode: string;
  dateOfSale: string;
  monthlyRental: number;
  devicesLicensed: number;
}

export interface PendingAdminApprovalDto {
  userId: number;
  firstName: string;
  lastName: string;
  email: string;
  companyId: number;
  companyName: string;
  registrationDate: string;
  registrationCode: string;
}

export interface BillingOverviewDto {
  companyId: number;
  companyName: string;
  devicesLicensed: number;
  currentDeviceCount: number;
  monthlyRental: number;
  dateOfSale?: string;
  hasActiveAdmin: boolean;
  adminName?: string;
}

export interface SuperAdminStats {
  totalCompanies: number;
  activeCompanies: number;
  pendingCompanies: number;
  totalUsers: number;
  activeUsers: number;
  totalDevices: number;
  activeDevices: number;
}

export interface CompanyUser {
  userId: number;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  createdDate: string;
  lastLoginDate?: string;
}

export interface CreateCompanyUserRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

export interface UpdateCompanyUserRequest {
  firstName?: string;
  lastName?: string;
  email?: string;
  newPassword?: string;
  isActive?: boolean;
}

export interface CompanyUserStats {
  totalUsers: number;
  activeUsers: number;
  recentLogins: number;
}

export const authApi = {
  login: async (request: LoginRequest): Promise<LoginResponse> => {
    const response = await api.post('/Auth/login', request);
    return response.data;
  },

  register: async (request: RegisterData): Promise<RegisterResponse> => {
    const response = await api.post('/Auth/register', request);
    return response.data;
  },

  refreshToken: async (token: string): Promise<LoginResponse> => {
    const response = await api.post('/Auth/refresh', { token });
    return response.data;
  },

  // Super Admin APIs - Enhanced
  getAllCompanies: async (): Promise<Company[]> => {
    const response = await api.get('/superadmin/companies');
    return response.data;
  },

  getPendingCompanies: async (): Promise<Company[]> => {
    const response = await api.get('/superadmin/companies/pending');
    return response.data;
  },

  // NEW: Get pending admin approvals
  getPendingAdminApprovals: async (): Promise<PendingAdminApprovalDto[]> => {
    const response = await api.get('/superadmin/companies/pending-admins');
    return response.data;
  },

  // NEW: Approve company admin
  approveCompanyAdmin: async (userId: number): Promise<void> => {
    await api.post(`/superadmin/approve-admin/${userId}`);
  },

  // NEW: Create customer company with billing info
  createCustomerCompany: async (companyData: CreateCustomerCompanyRequest): Promise<CreateCompanyResponse> => {
    const response = await api.post('/superadmin/companies', companyData);
    return response.data;
  },

  // NEW: Get billing overview
  getBillingOverview: async (): Promise<BillingOverviewDto[]> => {
    const response = await api.get('/superadmin/billing-overview');
    return response.data;
  },

  getHostingCompany: async (): Promise<Company> => {
    const companies = await authApi.getAllCompanies();
    const hostingCompany = companies.find(c => c.companyType === CompanyType.Hosting);
    if (!hostingCompany) {
      throw new Error('Hosting company not found');
    }
    return hostingCompany;
  },

  // Legacy methods - kept for backwards compatibility
  approveCompany: async (companyId: number): Promise<void> => {
    await api.post(`/superadmin/companies/${companyId}/approve`);
  },

  rejectCompany: async (companyId: number, reason: string): Promise<void> => {
    await api.post(`/superadmin/companies/${companyId}/reject`, { reason });
  },

  updateCompany: async (companyId: number, data: UpdateCompanyRequest): Promise<void> => {
    await api.put(`/superadmin/companies/${companyId}`, data);
  },

  deleteCompany: async (companyId: number): Promise<void> => {
  await api.delete(`/superadmin/companies/${companyId}`);
  },

  getCompanyUsers: async (companyId: number): Promise<CompanyUser[]> => {
    const response = await api.get(`/superadmin/companies/${companyId}/users`);
    return response.data;
  },

  getSuperAdminStats: async (): Promise<SuperAdminStats> => {
    const response = await api.get('/superadmin/stats');
    return response.data;
  },

  // Company User Management APIs
  getMyCompanyUsers: async (): Promise<CompanyUser[]> => {
    const response = await api.get('/companyusers');
    return response.data;
  },

  createCompanyUser: async (request: CreateCompanyUserRequest): Promise<CompanyUser> => {
    const response = await api.post('/companyusers', request);
    return response.data;
  },

  updateCompanyUser: async (userId: number, request: UpdateCompanyUserRequest): Promise<void> => {
    await api.put(`/companyusers/${userId}`, request);
  },

  deleteCompanyUser: async (userId: number): Promise<void> => {
    await api.delete(`/companyusers/${userId}`);
  },

  toggleUserStatus: async (userId: number): Promise<void> => {
    await api.post(`/companyusers/${userId}/toggle-status`);
  },

  getCompanyUserStats: async (): Promise<CompanyUserStats> => {
    const response = await api.get('/companyusers/stats');
    return response.data;
  },

  resendEmail: async (userId: number, emailType: string): Promise<void> => {
  await api.post(`/companyusers/${userId}/resend-email`, { emailType });
  }
};